import React, { createContext, useCallback, useContext, useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';

export const ActionsContext = createContext({});
export const CurrentItemContext = createContext({});
export const DataContext = createContext({});
export const IsAdministeringTestContext = createContext(undefined);
export const TimestampContext = createContext({});

export const PollingContext = ({ children, offline }) => {
  const [currentItem, setCurrentItem] = useState(undefined);
  const [currentItemTimestamp, setCurrentItemTimestamp] = useState(0);
  const [data, setData] = useState({}); // the latest copy of the api's data with local changes mixed on top.
  const [dataTimestamp, setDataTimestamp] = useState(0);
  const [pollInterval, setPollInterval] = useState(1000);
  const [isAdministeringTest, setIsAdministeringTest] = useState(undefined);
  const [sessionID, setSessionID] = useState(null);

  const source_poll = useRef(axios.CancelToken.source());
  const source_sendLocalChanges = useRef(axios.CancelToken.source());
  const localChanges = useRef({}); // changes to data triggered by the user/locally; until sync with api
  const localChanges_outgoingBuffer = useRef({}); // local changes are moved here while syncing with api
  const isSyncing = useRef(false); // mutex preventing the polling interval from racing a flush in progress

  const poll = useCallback(async () => {
    const data = {
      currentItemTimestamp,
      dataTimestamp,
      sessionID
    };

    const config = {
      cancelToken: source_poll.current.token,
      headers: {
        'Content-Type': 'application/json'
      },
      // 30s timeout — protects isSyncing from gating polling forever if the
      // network or server stalls. Axios rejects with a timeout error which
      // the .catch swallows; finally{} in syncWithApi clears isSyncing and
      // the next interval tick re-tries.
      timeout: 30000,
      // Polling is intentionally allowed to drop a beat. Tells error-middleware
      // not to fire a user-visible "Network error" toast for these failures —
      // otherwise a sustained outage produces one toast per second.
      _silent: true
    };

    let response = await axios
      .post(process.env.REACT_APP_API_URL + '/session/poll', data, config)
      .catch((error) => {
        console.log(error);
      });

    // .catch above returns undefined on a swallowed rejection; bail before
    // dereferencing response.data, otherwise we throw a TypeError every tick.
    if (!response) return undefined;
    if (!response.data) throw new Error('Failed to retrieve poll response data.');

    // Freshest merged data, returned to callers (e.g. updateDataAndFlush) so
    // they can inspect server-confirmed state synchronously instead of
    // reading the `data` context value, which won't reflect this update
    // until the next render.
    //
    // Defaults to localChanges rather than staying undefined: the server
    // omits `data`/`dataTimestamp` whenever the client's dataTimestamp is
    // already current (routes/session.js's /poll — nothing changed since
    // the last poll), which is the common case right after sendLocalChanges
    // above already persisted this same flush's write. That's a genuine
    // "request succeeded, nothing new" result, not a failure — callers like
    // WCAT's advanceAfterScoring (test-page.js) treat an undefined return as
    // "flush was cancelled/never reached the server" and deliberately stay
    // put, so leaving mergedData undefined here made every WCAT Next click
    // silently no-op whenever the write had already round-tripped.
    let mergedData = { ...localChanges.current };

    ReactDOM.unstable_batchedUpdates(() => {
      setPollInterval(response.data.pollingInterval);
      setIsAdministeringTest(response.data.isAdministeringTest);

      if (response.data.currentItemTimestamp !== undefined) {
        // set new currentItem values
        setCurrentItemTimestamp(response.data.currentItemTimestamp);
        setCurrentItem(response.data.currentItem);
      }

      if (response.data.dataTimestamp !== undefined) {
        // set new data values
        setDataTimestamp(response.data.dataTimestamp);
        mergedData = { ...response.data.data, ...localChanges.current };
        // clear local changes buffer
        if (Object.keys(localChanges_outgoingBuffer.current).length > 0)
          localChanges_outgoingBuffer.current = {};
        setData(mergedData);
      }

      if (response.data.isAdministeringTest === false) {
        // clear content and data, since there is no test
        setCurrentItem(undefined);
        setData({});
        localChanges.current = {};
        localChanges_outgoingBuffer.current = {};
        mergedData = {};
      }
    });

    return mergedData;
  }, [currentItemTimestamp, dataTimestamp, sessionID]);

  // cancel active requests and reset data
  const resetData = useCallback(() => {
    // TODO: test and decide on supporting online mode here.
    if (!offline)
      throw new Error('DataContext:resetData needs to be tested in online mode before use.');

    // cancel requests
    source_poll.current.cancel();
    source_sendLocalChanges.current.cancel();

    // reset vars to init values
    setData({});
    setPollInterval(1000);
    localChanges.current = {};
    localChanges_outgoingBuffer.current = {};
  }, [offline]);

  // send data changes to api
  const sendLocalChanges = useCallback(async () => {
    // don't do anything if there aren't any local changes
    if (Object.keys(localChanges.current).length === 0) return;

    // copy local changes to buffer
    localChanges_outgoingBuffer.current = { ...localChanges.current };

    // clear local changes
    localChanges.current = {};

    // TODO: modified buffer will need to go away when expressions refactors undefined to null
    const modifiedBuffer = {};
    for (const [key, value] of Object.entries(localChanges_outgoingBuffer.current)) {
      modifiedBuffer[key] = value === undefined ? null : localChanges_outgoingBuffer.current[key];
    }

    // send local changes to the api
    const requestData = {
      data: modifiedBuffer,
      sessionID: sessionID
    };

    const config = {
      cancelToken: source_sendLocalChanges.current.token,
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 30000, // matches poll timeout; prevents isSyncing from gating polling on a stalled setData
      _silent: true // see /session/poll above — same rationale
    };

    await axios
      .post(process.env.REACT_APP_API_URL + '/session/setData', requestData, config)
      .catch((error) => {
        // reset data timestamp to force update next poll
        setDataTimestamp(0);

        console.log(error);
      });
  }, [sessionID]);

  // upload local changes, then download latest data.
  // Guarded by isSyncing so the polling interval can't race a flush already in progress.
  // Returns poll()'s server-confirmed merged data (see poll() above) so callers
  // like updateDataAndFlush can inspect it synchronously.
  const syncWithApi = useCallback(async () => {
    isSyncing.current = true;
    try {
      await sendLocalChanges();
      return await poll();
    } finally {
      isSyncing.current = false;
    }
  }, [poll, sendLocalChanges]);

  // TODO: remove data from dep list so this doesn't trigger renders when used as a hook. will need to test everywhere it's used in a dep list to make sure the change doesn't break anything.
  // Synchronous callback for context subscribers to submit changes to data.
  // Use updateDataAndFlush below when the caller needs the change confirmed
  // by the server before proceeding (e.g. session navigation).
  const updateData = useCallback(
    (newData) => {
      const currentData = {
        ...data,
        ...localChanges_outgoingBuffer.current,
        ...localChanges.current
      };
      let dataWillChange = false;
      for (const [key, value] of Object.entries(newData)) {
        if (value !== currentData[key]) {
          dataWillChange = true;

          // apply new data to local changes
          localChanges.current[key] = value;
        }
      }

      setData((prevState) => {
        if (dataWillChange)
          return {
            ...prevState,
            ...localChanges_outgoingBuffer.current,
            ...localChanges.current
          };
        else return prevState;
      });
    },
    [data]
  );

  // Async navigation-flush variant: applies the local change, cancels any
  // in-flight poll AND in-flight setData, mints fresh CancelTokens, and
  // awaits syncWithApi so the caller doesn't proceed until the server has
  // confirmed our write. Prevents a concurrent interval-tick syncWithApi
  // from sending a duplicate sessionControl_* write that would advance past
  // the next subtest entirely (PIN-42).
  //
  // Note: cancel() aborts the *client* side only. If the cancelled setData
  // is already past the server's TCP read buffer, wici-api may still process
  // it and then process the post-flush write, resulting in two state
  // mutations. Robustness here depends on wici-api being idempotent on
  // sessionControl_* keys within the same currentItemTimestamp window. The
  // client-side navGuard ref in test-page.js (keyed on currentItemTimestamp)
  // prevents the ordinary path from generating a duplicate, but server-side
  // idempotency is the load-bearing guarantee for true safety.
  //
  // Returns the server-confirmed data (or undefined if offline or the flush
  // didn't reach poll()) so a caller can inspect it before deciding on a
  // follow-up action — e.g. WCAT's auto-advance (test-page.js) checks
  // discontinueTriggered here before dispatching sessionControl_nextItem, so
  // the discontinue overlay gets a render in between (PIN-193).
  const updateDataAndFlush = useCallback(
    async (newData) => {
      updateData(newData);
      if (offline) return undefined;
      source_poll.current.cancel();
      source_poll.current = axios.CancelToken.source();
      source_sendLocalChanges.current.cancel();
      source_sendLocalChanges.current = axios.CancelToken.source();
      return await syncWithApi();
    },
    [updateData, offline, syncWithApi]
  );

  // sync data with api on interval. Skip ticks when a flush (or prior tick)
  // is already syncing — re-entrant calls would race localChanges/buffer.
  useEffect(() => {
    if (offline) return;

    let newIntervalID = setInterval(() => {
      if (isSyncing.current) return;
      syncWithApi();
    }, pollInterval);
    return () => clearInterval(newIntervalID);
  }, [pollInterval, offline, syncWithApi]);

  return (
    <ActionsContext.Provider
      value={{ resetData, setCurrentItem, setSessionID, updateData, updateDataAndFlush }}
    >
      <CurrentItemContext.Provider value={currentItem}>
        <DataContext.Provider value={data}>
          <IsAdministeringTestContext.Provider value={isAdministeringTest}>
            <TimestampContext.Provider value={{ currentItemTimestamp, dataTimestamp }}>
              {children}
            </TimestampContext.Provider>
          </IsAdministeringTestContext.Provider>
        </DataContext.Provider>
      </CurrentItemContext.Provider>
    </ActionsContext.Provider>
  );
};

export const useCurrentItem = () => {
  const currentItem = useContext(CurrentItemContext);
  return currentItem;
};

export const useData = () => {
  const data = useContext(DataContext);
  if (!data) throw new Error('The "useData" hook must be used inside the PollingContext context.');
  return data;
};

export const useDataActions = () => {
  const actions = useContext(ActionsContext);
  if (!actions)
    throw new Error('The "useDataActions" hook must be used inside the PollingContext context.');
  return actions;
};

export const useIsAdministeringTest = () => {
  const isAdministeringTest = useContext(IsAdministeringTestContext);
  return isAdministeringTest;
};

export const useTimestamps = () => {
  const timestamps = useContext(TimestampContext);
  if (!timestamps)
    throw new Error('The "useTimestamps" hook must be used inside the PollingContext context.');
  return timestamps;
};
